<template>
  <div
    :id="'boss-modal-control-'+userId"
    class="reveal boss-modal-container-resume"
    data-reveal
    @scroll="onScroll"
  >
    <h2
      class="boss-title"
      data-test="resume-updated-date"
    >
      会員レジュメ（{{ resumeUpdateDate }}更新）
    </h2>
    <!-- ▼レジュメカード -->
    <section class="boss-resumecard-item">
      <!-- 詳細のアバターはチェックボックス必ず非表示のためfalse -->
      <scout-resume-card-avatar
        :id="userId"
        :member="member"
        :is-show-checkbox="false"
        :is-scout-decline="isScoutDecline"
      />
      <div class="boss-resumecard-item-right">
        <div class="boss-resumecard-details">
          <div class="boss-resumecard-overview clearfix">
            <scout-resume-member-summary
              class="boss-resumecard-overview-title"
              :member="member"
            />
            <dl class="boss-resumecard-overview-list clearfix">
              <dt class="boss-resumecard-overview-item">
                <span class="boss-resumecard-head">直近の経験職種</span>
              </dt>
              <dd
                class="boss-resumecard-overview-body boss-ellipsis-block"
                data-test="member_experienced_occupation"
              >
                {{ presentOccupation | parseBlankForDefaultValue }}
              </dd>
            </dl>
            <dl class="boss-resumecard-overview-list clearfix">
              <dt class="boss-resumecard-overview-item">
                <span class="boss-resumecard-head">希望職種</span>
              </dt>
              <dd
                class="boss-resumecard-overview-body boss-ellipsis-block"
                data-test="member_desired_occupation"
              >
                {{ convertObjectLabel(member.desiredOccupations) | parseBlankForDefaultValue }}
              </dd>
            </dl>
            <div class="boss-layout boss-layout-no-gap">
              <dl class="boss-resumecard-overview-list">
                <dt class="boss-resumecard-overview-item">
                  <span class="boss-resumecard-head">希望勤務地</span>
                </dt>
                <dd
                  class="boss-resumecard-overview-body boss-ellipsis-block"
                  data-test="member_desired_work_prefecture"
                >
                  {{ convertPrefectureLabel(member.desiredWorkPrefectures) | parseBlankForDefaultValue }}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </section>
    <!-- ▲レジュメカード -->
    <!-- ▼職務経歴書 -->
    <scout-member-career-resume
      v-if="!isMaskingTarget"
      :member="member"
    />
    <div
      v-else
      data-test="masking"
      class="margin-top-1"
    >
      {{ maskingLabel }}
    </div>
    <!-- ▲職務経歴書 -->
    <!-- ▼コメント -->
    <template v-if="showDetail">
      <scout-member-comment
        :user-id="userId"
        :comment="comment"
        @update-comment="updateComment"
      />
    </template>
    <!-- ▲コメント -->
    <!-- ▼早見表 -->
    <template v-if="contractId && showDetail">
      <scout-member-scout-summary
        :user-id="userId"
        :contract-id="contractId"
      />
    </template>
    <!-- ▲早見表 -->
    <!-- ▼ スカウト送信履歴 -->
    <template v-if="contractId && jobId && showDetail">
      <scout-member-scout-history
        :contract-id="contractId"
        :job-id="jobId"
        :user-id="userId"
        data-test="scout-history"
      />
    </template>
    <!-- ▲ スカウト送信履歴 -->
    <!-- ▼☓ボタン -->
    <button
      class="close-button boss-close-button boss-icon-cross"
      data-close
      aria-label="Close modal"
      type="button"
    />
    <!-- ▲☓ボタン -->
    <!-- ▼設定を閉じるボタン -->
    <div class="boss-modal-close">
      <button
        v-if="isShowCheckedCloseButton"
        class="boss-button-center boss-button-large boss-modal-selected-close-button"
        data-close
        aria-label="Close modal"
        type="button"
        data-test="checked-close-button"
        @click="check"
      >
        選択して閉じる
      </button>
      <button
        v-if="showsExcludedButton"
        class="boss-button-center boss-button-large"
        data-close
        aria-label="Close modal"
        type="button"
        data-test="excluded-close-button"
        @click="registerExcludedMember"
      >
        除外者へ
      </button>
      <button
        class="boss-button-center boss-button-large boss-modal-close-button"
        data-close
        aria-label="Close modal"
        type="button"
        data-test="close-button"
      >
        閉じる
      </button>
      <a
        v-scroll-to="{ element: `#boss-modal-control-${userId}`, container: `#boss-modal-control-${userId}`, duration: 500 }"
        class="boss-top-scroll"
        data-test="scroll-top"
      >
        <span class="boss-icon-triangle-up" />
        <span class="boss-top-scroll-text">上まで戻る</span>
      </a>
    </div>
    <!-- ▲設定を閉じるボタン -->
  </div>
</template>

<script>
import dayjs from 'dayjs'
import _ from 'lodash'
import ScoutResumeCardAvatar from '@/components/molecules/ScoutResumeCardAvatar'
import ScoutResumeMemberSummary from '@/components/atoms/ScoutResumeMemberSummary'
import ScoutMemberCareerResume from '@/components/molecules/ScoutMemberCareerResume'
import ScoutMemberScoutSummary from '@/components/molecules/ScoutMemberScoutSummary'
import ScoutMemberScoutHistory from '@/components/molecules/ScoutMemberScoutHistory'
import ScoutMemberComment from '@/components/molecules/ScoutMemberComment'
import resumeParseUtils from '@/lib/resumeParseUtils'
import { hideAllPoppers } from 'floating-vue'

export default {
  components: {
    ScoutResumeCardAvatar,
    ScoutResumeMemberSummary,
    ScoutMemberCareerResume,
    ScoutMemberScoutSummary,
    ScoutMemberScoutHistory,
    ScoutMemberComment
  },
  mixins: [resumeParseUtils],
  props: {
    jobId: {
      type: Number,
      default: null
    },
    userId: {
      type: Number,
      required: true
    },
    id: {
      type: Number,
      required: true
    },
    contractId: {
      type: Number,
      default: null
    },
    showDetail: {
      type: Boolean,
      default: false
    },
    isShowCheckedCloseButton: {
      type: Boolean,
      default: true
    },
    searchConditionName: {
      type: String,
      default: ''
    },
    showsExcludedButton: {
      type: Boolean,
      default: false
    },
    isScoutDecline: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      member: {},
      loading: true
    }
  },
  computed: {
    resumeUpdateDate () {
      return dayjs(this.member.updateDate).format('YYYY/MM/DD')
    },
    presentOccupation () {
      return _.get(this.member, 'presentOccupation.name')
    },
    isMaskingTarget () {
      return _.get(this.member, 'isScoutRejection') || !!_.get(this.member, 'withdrawDate') || !_.get(this.member, 'subscriptions.scout')
    },
    maskingLabel () {
      const label = _.get(this.member, 'withdrawDate') ? '退会済み' : '利用停止'
      return this.loading ? '' : `${label}のため表示できません。`
    },
    comment () {
      return _.get(this.member, 'approachComment')
    }
  },
  watch: {
    showDetail (val) {
      val && this.fetchMemberDetail(this.userId)
    },
    member (val) {
      this.loading = _.isEmpty(val)
    }
  },
  methods: {
    onScroll () {
      hideAllPoppers()
    },
    fetchMemberDetail (userId) {
      this.$http.secured.get(`/scout/view/users/${userId}`)
        .then(response => this.setProperty(response))
    },
    setProperty (response) {
      this.$set(this, 'member', response.data)
    },
    convertSliceObjectLabel (value, begin, end) {
      if (!value || !value.length) return ''
      return value.slice(begin, end).map(v => {
        return this.getNameOrUndefined(v, 'name')
      }).join('、')
    },
    convertObjectLabel (value) {
      if (!value || !value.length) return ''
      return value.map(v => {
        return this.getNameOrUndefined(v, 'name')
      }).join('、')
    },
    convertPrefectureLabel (value) {
      if (!value || !value.length) return ''
      return value.map(v => {
        const cityLabel = v.cities.map(city => city.name).join('、')
        return cityLabel.length ? `${this.getNameOrUndefined(v, 'name')}(${cityLabel})` : this.getNameOrUndefined(v, 'name')
      }).join('、')
    },
    convertSliceArrayLabel (value, begin, end) {
      if (!value) return ''
      return value.slice(begin, end).map(v => this.getNameOrUndefined(v, 'name')).join('、')
    },
    check () {
      this.$emit('check-from-child', this.id)
    },
    updateComment (comment) {
      this.$emit('update-comment', comment)
    },
    registerExcludedMember () {
      this.$http.secured.post('/scout/view/company/excluded_users', [{ userId: this.userId, searchConditionName: this.searchConditionName }]).then(() => {
        this.$toasted.global.success({ message: '除外者一覧へ登録しました。' })
        this.$set(this.member, 'isScoutExcluded', true)
        this.$emit('update-member-excluded')
      })
    }
  }
}
</script>
